import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MapPin, Search, Clock, Users, Compass, Crown, History, Gem, ArrowRight, Quote, Map, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import SEO from '../components/SEO';
import RouteMap from '../components/RouteMap';
import { AnimatedSection, AnimatedCard } from '../components/AnimatedComponents';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DestinationsPage = () => {
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [showMapModal, setShowMapModal] = useState(false);
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const highlightRef = useRef(null);
  const gridRef = useRef(null);

  useEffect(() => {
    const fetchDestinations = async () => {
      try {
        const res = await fetch(`${API_URL}/api/destinations`);
        if (!res.ok) throw new Error('Failed to fetch');
        const data = await res.json();
        setDestinations(data.map(d => ({ ...d, routes: d.routes || [] })));
      } catch (e) {
        console.error('Error fetching destinations:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchDestinations();
  }, []);

  const filteredDestinations = destinations.filter(dest => {
    if (!searchQuery.trim()) {
      // No search query - apply only category filter
      return selectedCategory === 'all' || dest.category === selectedCategory;
    }
    
    // Search in multiple fields
    const query = searchQuery.toLowerCase().trim();
    const matchesSearch = 
      dest.name?.toLowerCase().includes(query) || 
      dest.country?.toLowerCase().includes(query) ||
      dest.tagline?.toLowerCase().includes(query) ||
      dest.category?.toLowerCase().includes(query);
    
    const matchesCategory = selectedCategory === 'all' || dest.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const openMapModal = (dest) => { setSelectedDestination(dest); setShowMapModal(true); };

  const scrollCarousel = (ref, direction) => {
    if (!ref.current) return;
    const amount = ref.current.clientWidth * 0.75;
    ref.current.scrollBy({ left: direction === 'right' ? amount : -amount, behavior: 'smooth' });
  };

  const heritageHighlights = [
    { title: "Ancient Temples of Bali", subtitle: "Sacred Heritage", description: "Discover centuries-old temples nestled among rice terraces, where Balinese spirituality comes alive.", image: "https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=800&q=80", experience: "Temple ceremonies, traditional offerings, sunrise rituals" },
    { title: "Royal Palaces of Kerala", subtitle: "Living History", description: "Walk through magnificent palaces that echo with stories of maharajas and ancient trading empires.", image: "https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?w=800&q=80", experience: "Palace tours, Kathakali performances, Ayurvedic traditions" },
    { title: "Desert Fortresses of Dubai", subtitle: "Arabian Nights", description: "Experience the contrast of ancient Bedouin culture against the backdrop of modern marvels.", image: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=800&q=80", experience: "Heritage villages, desert camps, traditional souks" }
  ];

  return (
    <div className="min-h-screen bg-s-bg">
      <SEO title="Heritage Destinations - Sentora Holidays | Timeless Journeys Since 1985" description="Explore world heritage destinations with Sentora Holidays. 40 years of crafting culturally immersive experiences." />

      {/* Hero */}
      <section className="relative h-[50vh] md:h-[70vh] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1548013146-72479768bada?w=1920&q=80)' }} />
        <div className="absolute inset-0 bg-gradient-to-b from-s-bg/60 via-s-bg/40 to-s-bg" />
        <div className="absolute top-0 left-0 w-full h-1" style={{ background: 'linear-gradient(to right, transparent, rgb(var(--color-accent)), transparent)', opacity: 0.5 }} />
        <div className="absolute bottom-0 left-0 w-full h-px" style={{ background: 'linear-gradient(to right, transparent, rgb(var(--color-accent)), transparent)', opacity: 0.3 }} />

        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-4 md:mb-6 hero-text-reveal">
                <div className="h-px w-8 md:w-12 bg-s-accent" />
                <span className="text-s-accent text-xs md:text-sm tracking-[0.2em] md:tracking-[0.3em] uppercase font-light">Heritage Collection</span>
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-s-text mb-4 md:mb-6 leading-tight font-heritage hero-text-reveal">
                Journeys Through
                <span className="block italic text-s-accent mt-1 md:mt-2">Timeless Wonders</span>
              </h1>
              <p className="text-sm md:text-lg lg:text-xl text-s-text2 mb-6 md:mb-8 leading-relaxed max-w-2xl hero-text-reveal-delay">
                Immerse yourself in destinations where history breathes, traditions thrive, and every moment connects you to centuries of human heritage.
              </p>
              <div className="flex flex-wrap items-center gap-3 md:gap-6 text-xs md:text-sm text-s-text2 hero-text-reveal-delay">
                <div className="flex items-center gap-1.5 md:gap-2"><History className="w-4 h-4 md:w-5 md:h-5 text-s-accent" /><span>UNESCO Heritage Sites</span></div>
                <div className="flex items-center gap-1.5 md:gap-2"><Crown className="w-4 h-4 md:w-5 md:h-5 text-s-accent" /><span>Royal Experiences</span></div>
                <div className="flex items-center gap-1.5 md:gap-2"><Gem className="w-4 h-4 md:w-5 md:h-5 text-s-accent" /><span>Cultural Immersion</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="py-8 md:py-12 border-b border-s-border">
        <AnimatedSection className="max-w-4xl mx-auto px-4 text-center">
          <Quote className="w-8 h-8 md:w-10 md:h-10 text-s-primary/30 mx-auto mb-3 md:mb-4" />
          <blockquote className="text-base md:text-xl lg:text-2xl text-s-text2 font-light italic leading-relaxed">"The world is a book, and those who do not travel read only one page."</blockquote>
          <cite className="text-s-accent text-xs md:text-sm mt-3 md:mt-4 block tracking-wide">— Saint Augustine</cite>
        </AnimatedSection>
      </section>

      {/* Search */}
      <section className="py-8 md:py-12 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="bg-s-bg2/50 backdrop-blur border border-s-border rounded-2xl p-4 md:p-6 lg:p-8">
            <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-stretch md:items-center">
              <div className="flex-1 w-full">
                <label className="text-s-accent text-[10px] md:text-xs tracking-[0.2em] uppercase mb-1.5 md:mb-2 block">Search Destinations</label>
                <div className="relative">
                  <Compass className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 text-s-muted" size={18} />
                  <Input placeholder="Search by destination, country, or experience..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} data-testid="destination-search" className="pl-10 md:pl-12 h-11 md:h-14 bg-s-card/50 border-s-border text-s-text placeholder:text-s-muted text-sm md:text-lg rounded-xl" />
                </div>
              </div>
              <div className="flex gap-2 w-full md:w-auto overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
                {[{ key: 'all', label: 'All' }, { key: 'international', label: 'International' }, { key: 'domestic', label: 'India' }].map((cat) => (
                  <Button key={cat.key} data-testid={`filter-${cat.key}`} variant={selectedCategory === cat.key ? 'default' : 'outline'} onClick={() => setSelectedCategory(cat.key)}
                    className={`flex-1 md:flex-none h-10 md:h-12 text-xs md:text-sm ${selectedCategory === cat.key ? 'bg-s-primary hover:bg-s-primary-h text-white border-0' : 'border-s-border text-s-text2 hover:text-s-text hover:border-s-primary/50 bg-transparent'}`}>
                    {cat.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Heritage Highlights - Carousel */}
      <section className="py-12 md:py-16 px-4 border-t border-s-border">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection className="text-center mb-8 md:mb-12">
            <div className="flex items-center justify-center gap-3 mb-3 md:mb-4">
              <div className="h-px w-10 md:w-16 bg-gradient-to-r from-transparent to-s-primary" />
              <Crown className="w-5 h-5 md:w-6 md:h-6 text-s-accent" />
              <div className="h-px w-10 md:w-16 bg-gradient-to-l from-transparent to-s-primary" />
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-s-text mb-2 md:mb-3 font-heritage">Heritage <span className="italic text-s-accent">Experiences</span></h2>
            <p className="text-s-text2 text-sm md:text-base max-w-2xl mx-auto">Curated journeys to the world's most culturally significant destinations.</p>
          </AnimatedSection>
          <div className="relative group">
            <button onClick={() => scrollCarousel(highlightRef, 'left')} className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-black/80 hover:bg-black p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity -translate-x-2"><ChevronLeft className="text-white" size={20} /></button>
            <button onClick={() => scrollCarousel(highlightRef, 'right')} className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-black/80 hover:bg-black p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity translate-x-2"><ChevronRight className="text-white" size={20} /></button>
            <div ref={highlightRef} className="flex gap-4 md:gap-6 overflow-x-auto pb-4 scroll-smooth snap-x snap-mandatory" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
              {heritageHighlights.map((highlight, idx) => (
                <div key={idx} className="flex-shrink-0 w-[85vw] sm:w-[70vw] md:w-[380px] snap-start group/card relative overflow-hidden rounded-2xl bg-s-bg2 border border-s-border hover:border-s-primary/30 transition-all duration-500">
                  <div className="relative h-48 md:h-64 overflow-hidden">
                    <img loading="lazy" src={highlight.image} alt={highlight.title} className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-s-bg2 via-s-bg2/50 to-transparent" />
                    <Badge className="absolute top-3 md:top-4 left-3 md:left-4 bg-s-primary/90 text-white border-0 text-[10px] md:text-xs tracking-wider">{highlight.subtitle}</Badge>
                  </div>
                  <div className="p-4 md:p-6">
                    <h3 className="text-base md:text-xl font-medium text-s-text mb-1.5 md:mb-2 group-hover/card:text-s-accent transition-colors font-heritage">{highlight.title}</h3>
                    <p className="text-s-text2 text-xs md:text-sm leading-relaxed mb-3 md:mb-4">{highlight.description}</p>
                    <div className="pt-3 md:pt-4 border-t border-s-border">
                      <p className="text-[10px] md:text-xs text-s-accent/80 tracking-wide uppercase mb-1">Experiences Include</p>
                      <p className="text-s-muted text-xs md:text-sm">{highlight.experience}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Destinations - Carousel */}
      <section className="py-12 md:py-16 px-4 bg-gradient-to-b from-s-bg to-s-bg2">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection className="flex items-center justify-between mb-6 md:mb-10">
            <div>
              <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-s-text font-heritage">All <span className="italic text-s-accent">Destinations</span></h2>
              <p className="text-s-muted text-xs md:text-sm mt-1">{filteredDestinations.length} curated experiences</p>
            </div>
          </AnimatedSection>
          <div className="relative group">
            <button onClick={() => scrollCarousel(gridRef, 'left')} className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-black/80 hover:bg-black p-2 lg:p-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity -translate-x-3"><ChevronLeft className="text-white" size={20} /></button>
            <button onClick={() => scrollCarousel(gridRef, 'right')} className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-black/80 hover:bg-black p-2 lg:p-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity translate-x-3"><ChevronRight className="text-white" size={20} /></button>
            <div ref={gridRef} className="flex gap-3 md:gap-5 overflow-x-auto pb-4 scroll-smooth snap-x snap-mandatory" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
              {filteredDestinations.map((dest) => (
                <Link to="/packages" key={dest.id} data-testid={`destination-card-${dest.id}`} className="flex-shrink-0 w-[75vw] sm:w-[55vw] md:w-[300px] lg:w-[280px] snap-start">
                  <Card className="group/card overflow-hidden bg-s-bg2 border-s-border hover:border-s-primary/30 transition-all duration-500 cursor-pointer h-full">
                    <div className="relative h-44 md:h-56 overflow-hidden">
                      <img loading="lazy" src={dest.image} alt={dest.name} className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-700" />
                      <div className="absolute inset-0 bg-gradient-to-t from-s-bg2 via-s-bg2/30 to-transparent" />
                      <div className="absolute top-3 md:top-4 right-3 md:right-4">
                        <Badge className={`border-0 text-[10px] md:text-xs tracking-wider ${dest.category === 'international' ? 'bg-s-primary/80 text-s-primary' : 'bg-emerald-900/80 text-emerald-300'}`}>
                          {dest.category === 'international' ? 'International' : 'India'}
                        </Badge>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-3 md:p-5">
                        <h3 className="text-lg md:text-2xl text-white font-heritage">{dest.name}</h3>
                        <p className="text-s-text2 text-xs md:text-sm flex items-center gap-1"><MapPin size={12} className="text-s-accent" />{dest.country}</p>
                      </div>
                    </div>
                    <CardContent className="p-3 md:p-5 border-t border-s-border">
                      <p className="text-s-text2 text-xs md:text-sm mb-3 md:mb-4 italic">"{dest.tagline}"</p>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-[10px] md:text-xs text-s-muted uppercase tracking-wide">Duration</span>
                          <p className="text-s-text2 text-xs md:text-sm">{dest.duration}</p>
                        </div>
                        <div className="text-right">
                          <span className="text-[10px] md:text-xs text-s-muted uppercase tracking-wide">Best For</span>
                          <p className="text-s-text2 text-xs md:text-sm">{dest.best_time || 'All Year'}</p>
                        </div>
                      </div>
                      <div className="mt-3 md:mt-4 pt-3 md:pt-4 border-t border-s-border flex items-center justify-between">
                        <button onClick={(e) => { e.preventDefault(); openMapModal(dest); }} className="text-[10px] md:text-xs text-s-text2 hover:text-s-accent flex items-center gap-1 transition-colors">
                          <Map size={12} /> View Route
                        </button>
                        <span className="text-s-accent text-xs md:text-sm flex items-center gap-1 group-hover/card:gap-2 transition-all">Explore <ArrowRight size={12} /></span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
          {filteredDestinations.length === 0 && (
            <div className="text-center py-12 md:py-16">
              <Compass className="w-12 h-12 md:w-16 md:h-16 text-s-border mx-auto mb-4" />
              <p className="text-base md:text-xl text-s-muted">No destinations found matching your search.</p>
              <p className="text-s-muted text-sm mt-2">Try adjusting your filters or search terms.</p>
            </div>
          )}
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-12 md:py-16 px-4 border-t border-s-border">
        <div className="max-w-5xl mx-auto text-center">
          <AnimatedSection className="mb-6 md:mb-8">
            <div className="flex items-center justify-center gap-3 mb-4 md:mb-6">
              <div className="h-px w-8 md:w-12 bg-s-primary/50" />
              <span className="text-s-accent/80 text-[10px] md:text-xs tracking-[0.2em] md:tracking-[0.3em] uppercase">The Sentora Holidays Promise</span>
              <div className="h-px w-8 md:w-12 bg-s-primary/50" />
            </div>
            <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-s-text font-heritage">For the <span className="italic text-s-accent">Discerning Traveler</span></h2>
          </AnimatedSection>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            {[
              { icon: Crown, title: "Handpicked Hotels", desc: "Heritage properties & luxury stays" },
              { icon: Users, title: "Expert Guides", desc: "Culturally versed local experts" },
              { icon: History, title: "Authentic Access", desc: "Private heritage experiences" },
              { icon: Clock, title: "40 Years Trust", desc: "Experience creators since 1985" }
            ].map((item, idx) => (
              <AnimatedCard key={idx} delay={idx * 100} hoverEffect="lift-subtle" className="group">
                <div className="w-10 h-10 md:w-14 md:h-14 mx-auto mb-3 md:mb-4 rounded-full border border-s-primary/30 flex items-center justify-center group-hover:border-s-accent group-hover:bg-s-accent/10 transition-all">
                  <item.icon className="w-4 h-4 md:w-6 md:h-6 text-s-accent" />
                </div>
                <h3 className="text-s-text font-medium text-sm md:text-base mb-0.5 md:mb-1">{item.title}</h3>
                <p className="text-s-muted text-xs md:text-sm">{item.desc}</p>
              </AnimatedCard>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-20 px-4 bg-gradient-to-b from-s-bg2 to-s-bg">
        <AnimatedSection className="max-w-3xl mx-auto text-center">
          <span className="text-s-accent/60 text-xs md:text-sm tracking-[0.2em] md:tracking-[0.3em] uppercase">Begin Your Journey</span>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-s-text mb-4 md:mb-6 mt-3 md:mt-4 font-heritage">Let Us Craft Your <span className="block italic text-s-accent mt-1 md:mt-2">Heritage Experience</span></h2>
          <p className="text-s-text2 text-sm md:text-base mb-6 md:mb-8 leading-relaxed">Our experience curators specialize in creating culturally immersive journeys that honor tradition while ensuring your comfort at every step.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-3 md:gap-4">
            <Link to="/contact"><Button size="lg" className="bg-s-primary hover:bg-s-primary-h text-white px-6 md:px-8 h-12 md:h-14 text-sm md:text-base rounded-full w-full sm:w-auto btn-animate">Speak with an Expert</Button></Link>
            <Link to="/ai-planner"><Button size="lg" variant="outline" className="border-s-primary/50 text-s-accent hover:bg-s-primary/10 px-6 md:px-8 h-12 md:h-14 text-sm md:text-base rounded-full w-full sm:w-auto btn-animate">Plan with AI</Button></Link>
          </div>
        </AnimatedSection>
      </section>

      {/* Map Modal */}
      <Dialog open={showMapModal} onOpenChange={setShowMapModal}>
        <DialogContent className="bg-s-bg2 border-s-border text-s-text max-w-[95vw] md:max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-sm md:text-base"><Map className="w-4 h-4 md:w-5 md:h-5 text-s-accent" />{selectedDestination?.name} - Route Map</DialogTitle>
          </DialogHeader>
          {selectedDestination?.routes && (
            <div className="space-y-3 md:space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <p className="text-s-accent font-medium text-sm md:text-base">{selectedDestination.routes[0]?.name}</p>
                  <p className="text-s-text2 text-xs md:text-sm">{selectedDestination.duration} · {selectedDestination.routes[0]?.stops?.length} stops</p>
                </div>
                <div className="flex gap-3 md:gap-4 text-xs">
                  <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-s-success"></div><span className="text-s-text2">Start</span></div>
                  <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-s-accent"></div><span className="text-s-text2">Stop</span></div>
                  <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-s-error"></div><span className="text-s-text2">End</span></div>
                </div>
              </div>
              <RouteMap routes={selectedDestination.routes} height="300px" />
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-28 overflow-y-auto">
                {selectedDestination.routes[0]?.stops?.map((stop, idx) => (
                  <div key={idx} className="bg-s-card/50 p-2 rounded-lg text-xs"><p className="text-s-text font-medium truncate">{stop.name}</p><p className="text-s-muted">Day {stop.day}</p></div>
                ))}
              </div>
              <div className="flex justify-end gap-2 md:gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowMapModal(false)} className="border-s-border text-s-text2 text-xs md:text-sm">Close</Button>
                <Link to="/packages"><Button className="bg-s-primary hover:bg-s-primary-h text-xs md:text-sm">View Packages</Button></Link>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DestinationsPage;
